"use client";

import {createContext, type JSX, type ReactNode, useContext, useMemo} from "react";

import {getNestedValue, interpolate} from "./interpolate";
import type {Dictionary, Locale, TranslateFunction} from "./types";

/**
 * I18n context value
 */
interface I18nContextValue {
    /** Current locale */
    locale: Locale;
    /** Full dictionary object */
    dict: Dictionary;
    /** Translation function */
    t: TranslateFunction;
}

const I18nContext = createContext<I18nContextValue | null>(null);

/**
 * I18n Provider props
 */
interface I18nProviderProps {
    /** Current locale */
    locale: Locale;
    /** Dictionary loaded on server */
    dict: Dictionary;
    /** Children components */
    children: ReactNode;
}

/**
 * I18n Provider component
 * Wraps the app and provides translation context to all client components
 *
 * @example
 * // In layout.tsx (Server Component)
 * const dict = await getDictionary(lang);
 * return (
 *   <I18nProvider locale={lang} dict={dict}>
 *     {children}
 *   </I18nProvider>
 * );
 */
export function I18nProvider({locale, dict, children}: I18nProviderProps): JSX.Element {
    const value = useMemo<I18nContextValue>(
        () => ({
            locale,
            dict,
            t: (key, params) => {
                const template = getNestedValue(dict as unknown as Record<string, unknown>, key);
                return interpolate(template, params, locale);
            },
        }),
        [locale, dict],
    );

    return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/**
 * Hook to access i18n context
 * Must be used within I18nProvider
 *
 * @throws Error if used outside I18nProvider
 */
export function useI18nContext(): I18nContextValue {
    const ctx = useContext(I18nContext);
    if (!ctx) {
        throw new Error("useI18nContext must be used within I18nProvider. Make sure your component is wrapped in I18nProvider.");
    }
    return ctx;
}
